/**
 * Pure, unit-testable rotation logic shared by `log-store-writer.ts`.
 *
 * Deliberately operates on `{id, timestamp}` pairs only (never full `LogEntry` objects) so it is
 * cheap to keep an entire store's worth of metadata in memory while deciding what to evict.
 *
 * @see Docs/app-logs.md#aggregator--rotation-design
 */

export interface LogEntryMeta {
  id: number;
  timestamp: number;
}

export interface RotationLimits {
  /** Hard cap on the number of entries kept; the oldest surplus is evicted once this is hit. */
  maxEntries: number;
  /** Entries older than this many days are evicted regardless of how few entries exist. */
  maxAgeDays: number;
}

/**
 * Returns the ids to delete so the store respects both `limits.maxEntries` and
 * `limits.maxAgeDays` — whichever bound is hit first drives eviction (hybrid rotation policy).
 *
 * @param allMeta Metadata for every entry currently in the store.
 * @param limits The configured count/age caps.
 * @param now Injectable for deterministic tests; defaults to `Date.now()`.
 */
export function pickIdsToEvict(allMeta: LogEntryMeta[], limits: RotationLimits, now: number = Date.now()): number[] {
  if (allMeta.length === 0) {
    return [];
  }

  const maxAgeMs = limits.maxAgeDays * 24 * 60 * 60 * 1000;
  const cutoffTimestamp = now - maxAgeMs;

  const oldestFirst = [...allMeta].sort((a, b) => a.timestamp - b.timestamp);

  const evictedIds = new Set<number>();

  // Age cap: anything older than the cutoff goes, regardless of the count cap.
  for (const meta of oldestFirst) {
    if (meta.timestamp < cutoffTimestamp) {
      evictedIds.add(meta.id);
    }
  }

  // Count cap: among what survived the age cap, evict the oldest surplus over maxEntries.
  const survivors = oldestFirst.filter(meta => !evictedIds.has(meta.id));
  const surplus = survivors.length - limits.maxEntries;
  for (let i = 0; i < surplus; i++) {
    evictedIds.add(survivors[i].id);
  }

  return Array.from(evictedIds);
}
